import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  UncontrolledDropdown,
} from "reactstrap"
import ReactChartkick, { PieChart } from "react-chartkick"

import Chart from "chart.js"
import { withTranslation } from "react-i18next"
import { Loading, TranslatedTable } from "../../components"
import { setlistsize, fail } from "../../actions"
import { connect } from "react-redux"
import React from "react"
import axios from "axios"
import moment from "moment"
import { sumBy, topSlices, groupRows } from "../../lib/chartData"

ReactChartkick.addAdapter(Chart)

// How the Items tab may aggregate the usage records. "detail" keeps the
// original per-meter rows; the rest group the same records the charts use.
const ITEM_GROUPS = [
  { id: "detail", translation: "_detailedRows", field: null },
  {
    id: "subscription",
    translation: "_subscription",
    field: "entitlementName",
  },
  {
    id: "resourceGroup",
    translation: "_resourceGroup",
    field: "resourceGroupName",
  },
  { id: "resourceType", translation: "_resourceType", field: "resourceType" },
]

class OrderDetails extends React.Component {
  state = {
    fetching: true,
    invoiceIndex: 0,
    invoiceId: "latest",
    items: [],
    usageFetching: true,
    chartGroup: {
      id: "category",
      translation: "_category",
    },
    subscriptionChartData: {},
    categoryChartData: {},
    resourceGroupChartData: {},
    usageItems: [],
    itemsGroup: "detail",
    activeTab: "1",
  }

  // Built from props on every render. It used to be frozen into state in
  // componentDidMount, which runs before the parent has fetched the invoice
  // list - so the month picker kept whatever happened to be in the store at
  // that moment and never grew when the invoices arrived.
  invoiceOptions = () => [
    { id: "latest", index: 0, label: "Current month" },
    ...this.props.invoices.invoices
      .filter(
        (i) =>
          i.id.toLowerCase().startsWith("g") ||
          this.props.order.offerName !== "Azure plan"
      )
      .map((i, index) => ({
        id: i.id,
        index: index + 1,
        label: `${moment(i.billingPeriodStartDate).format(
          "MMM Do YYYY"
        )} - ${moment(i.billingPeriodEndDate).format("MMM Do YYYY")}`,
      })),
  ]

  componentDidMount = async () => {
    try {
      const { data } = await axios
        .get(`/orders/${this.props.order.id}`)
        .catch((error) =>
          this.props
            .fail(null, error)
            .then(() => this.setState({ usageFetching: false }))
        )
      this.setState({ items: data.items })

      this.getRgUsage("latest")

      this.updateTotal(data.items)
      this.makeChartData(data.items)
    } catch (err) {
      console.error(err)
    }

    this.setState({ fetching: false })
  }

  getRgUsage = (invoiceId) =>
    axios
      .get(`/orders/${this.props.order.id}/${invoiceId}/resourcegroup`)
      .then(({ data }) => {
        if (this.state.invoiceId === invoiceId) {
          this.setState({
            // kept so the Items tab can group the same records
            usageItems: data.items || [],
            subscriptionChartData: sumBy(data.items, "entitlementName"),
            // a customer's usage can span nearly two hundred resource groups;
            // a pie with that many slices says nothing and runs out of colours
            resourceGroupChartData: topSlices(
              sumBy(data.items, "resourceGroupName"),
              12,
              this.props.t("_other")
            ),
            usageFetching: false,
          })
        }
      })
      .catch((error) =>
        this.props
          .fail(null, error)
          .then(() => this.setState({ usageFetching: false }))
      )

  updateTotal = (items) => {
    const totalCost = (items || []).reduce(
      (acc, curr) => acc + (Number(curr.totalCost) || 0),
      0
    )

    this.setState({ totalCost: totalCost.toFixed(2) })
  }

  makeChartData = (items) => {
    this.setState({ categoryChartData: sumBy(items, "category") })
  }

  changeMonth = async (direction) => {
    this.setState({ fetching: true, usageFetching: true, usage: null })
    const increment = direction === "prev" ? 1 : -1

    // Clamp to invoices list length
    const invoices = this.invoiceOptions()
    const newIndex = Math.min(
      Math.max(0, this.state.invoiceIndex + increment),
      invoices.length - 1
    )

    this.setState({ invoiceIndex: newIndex })

    if (newIndex === 0) {
      return this.componentDidMount()
    }

    const invoice = invoices.find((i) => i.index === newIndex)

    try {
      const { data } = await axios.get(
        `/orders/${this.props.order.id}/invoice/${invoice.id}`
      )

      this.setState({ items: data.items, invoiceId: invoice.id })

      this.getRgUsage(invoice.id)

      this.updateTotal(data.items)
      this.makeChartData(data.items)
    } catch (err) {
      this.props
        .fail(null, err)
        .then(() => this.setState({ error: true, fetching: false }))
    }

    this.setState({ fetching: false })
  }

  toggleTab = (tab) =>
    this.state.activeTab !== tab ? this.setState({ activeTab: tab }) : null

  toggleChart = (group) => this.setState({ chartGroup: group })

  render = () => {
    const { t } = this.props
    const invoices = this.invoiceOptions()
    const invoice =
      invoices.find((i) => i.index === this.state.invoiceIndex) || invoices[0]

    const columns = [
      {
        Header: t("_name"),
        accessor: "name",
      },
      {
        Header: t("_category"),
        accessor: "category",
      },
      {
        Header: t("_subcategory"),
        accessor: "subcategory",
      },
      {
        Header: t("_quantityUsed"),
        accessor: "quantityUsed",
      },
      {
        Header: t("_unit"),
        accessor: "unit",
      },
      {
        Header: t("_totalCost"),
        accessor: "totalCost",
        Cell: (props) =>
          props.original.totalCost < 0.01
            ? "< 0.01"
            : props.original.totalCost.toFixed(2),
      },
    ]

    const itemsGroup =
      ITEM_GROUPS.find((g) => g.id === this.state.itemsGroup) || ITEM_GROUPS[0]

    const groupedColumns = [
      {
        Header: t(itemsGroup.translation),
        accessor: "key",
      },
      {
        Header: t("_rows"),
        accessor: "count",
        maxWidth: 120,
      },
      {
        Header: t("_totalCost"),
        accessor: "totalCost",
        maxWidth: 160,
        Cell: (props) =>
          props.original.totalCost < 0.01
            ? "< 0.01"
            : props.original.totalCost.toFixed(2),
      },
    ]

    return (
      <>
        <Row>
          <Col sm={{ size: 4, offset: 4 }}>
            {this.state.fetching ? (
              <Loading small />
            ) : (
              <h1 className="text-center">{this.state.totalCost} EUR</h1>
            )}
          </Col>
        </Row>
        <fieldset disabled={this.state.fetching}>
          <Row>
            <Col
              sm={{ size: 4, offset: 4 }}
              className="d-flex justify-content-between align-items-center"
            >
              <Button
                color="primary"
                onClick={() => this.changeMonth("prev")}
                disabled={this.state.invoiceIndex === invoices.length - 1}
              >
                &lt;
              </Button>
              <span>{invoice.label}</span>
              <Button
                color="primary"
                onClick={() => this.changeMonth("next")}
                disabled={this.state.invoiceIndex === 0}
              >
                &gt;
              </Button>
            </Col>
          </Row>
        </fieldset>

        <Nav tabs>
          <NavItem>
            <NavLink
              className={this.state.activeTab === "1" ? "active" : ""}
              onClick={() => {
                this.toggleTab("1")
              }}
            >
              {t("_chart")}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={this.state.activeTab === "2" ? "active" : ""}
              onClick={() => {
                this.toggleTab("2")
              }}
            >
              {t("_items")}
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={this.state.activeTab}>
          <br />
          <TabPane tabId="1">
            <Card>
              <CardHeader>
                {t("_usageGroupedBy")}{" "}
                <UncontrolledDropdown tag="span">
                  <DropdownToggle
                    tag="span"
                    style={{ cursor: "pointer" }}
                    caret
                  >
                    {t(this.state.chartGroup.translation)}
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem
                      onClick={() =>
                        this.toggleChart({
                          translation: "_category",
                          id: "category",
                        })
                      }
                    >
                      {t("_category")}
                    </DropdownItem>
                    <DropdownItem
                      onClick={() =>
                        this.toggleChart({
                          translation: "_subscription",
                          id: "subscription",
                        })
                      }
                    >
                      {t("_subscription")}
                    </DropdownItem>
                    <DropdownItem
                      onClick={() =>
                        this.toggleChart({
                          translation: "_resourceGroup",
                          id: "resource group",
                        })
                      }
                    >
                      {t("_resourceGroup")}
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </CardHeader>
              <CardBody>
                {this.state.fetching ? (
                  <Loading />
                ) : this.state.chartGroup.id === "subscription" ? (
                  <>
                    {this.state.invoiceId !== "latest" && (
                      <p className="text-muted text-center">
                        {t("_currentUsageOnly")}
                      </p>
                    )}
                    <PieChart
                      data={this.state.subscriptionChartData}
                      messages={{
                        empty: t("_noBillingCycle"),
                      }}
                      legend="bottom"
                      colors={chartColors}
                      height="500px"
                    />
                  </>
                ) : this.state.chartGroup.id === "category" ? (
                  <PieChart
                    data={this.state.categoryChartData}
                    messages={{
                      empty: t("_noBillingCycle"),
                    }}
                    legend="bottom"
                    colors={chartColors}
                    height="500px"
                  />
                ) : this.state.usageFetching ? (
                  <>
                    <p className="text-muted text-center">
                      {t("_thisWillTakeAWhile")}
                    </p>
                    <Loading />
                  </>
                ) : (
                  <>
                    <p className="text-muted text-center">
                      {t("_resourceGroupUsage")}
                      {this.state.invoiceId !== "latest"
                        ? ` ${t("_currentUsageOnly")}`
                        : ""}
                    </p>
                    <PieChart
                      data={this.state.resourceGroupChartData}
                      messages={{
                        empty: t("_noBillingCycle"),
                      }}
                      legend="right"
                    />
                  </>
                )}
              </CardBody>
            </Card>
          </TabPane>
          <TabPane tabId="2">
            <Card>
              <CardHeader>
                {t("_usageGroupedBy")}{" "}
                <UncontrolledDropdown tag="span">
                  <DropdownToggle
                    tag="span"
                    style={{ cursor: "pointer" }}
                    caret
                  >
                    {t(itemsGroup.translation)}
                  </DropdownToggle>
                  <DropdownMenu>
                    {ITEM_GROUPS.map((g) => (
                      <DropdownItem
                        key={g.id}
                        onClick={() => this.setState({ itemsGroup: g.id })}
                      >
                        {t(g.translation)}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </UncontrolledDropdown>
              </CardHeader>
              <CardBody>
                {itemsGroup.id !== "detail" &&
                  this.state.invoiceId !== "latest" && (
                    <p className="text-muted text-center">
                      {t("_currentUsageOnly")}
                    </p>
                  )}
                {itemsGroup.id === "detail" ? (
                  <TranslatedTable
                    data={this.state.items}
                    fetching={this.state.fetching}
                    columns={columns}
                    handleresize={this.props.setlistsize}
                    listSize={this.props.list.listSetting}
                    noDataText="No usage during current billing cycle"
                  />
                ) : (
                  <TranslatedTable
                    data={groupRows(this.state.usageItems, itemsGroup.field)}
                    fetching={this.state.usageFetching}
                    columns={groupedColumns}
                    handleresize={this.props.setlistsize}
                    listSize={this.props.list.listSetting}
                    noDataText="No usage during current billing cycle"
                  />
                )}
              </CardBody>
            </Card>
          </TabPane>
        </TabContent>
      </>
    )
  }
}

const colorList = [
  "#3366CC",
  "#DC3912",
  "#FF9900",
  "#109618",
  "#990099",
  "#3B3EAC",
  "#0099C6",
  "#DD4477",
  "#66AA00",
  "#B82E2E",
  "#316395",
  "#994499",
  "#22AA99",
  "#AAAA11",
  "#6633CC",
  "#E67300",
  "#8B0707",
  "#329262",
  "#5574A6",
  "#651067",
]

const chartColors = colorList
  .concat(colorList)
  .concat(colorList)
  .concat(colorList)
  .concat(colorList)

export default connect((s) => s, { setlistsize, fail })(
  withTranslation()(OrderDetails)
)
